import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSmartInsights } from '../SmartInsightsContext';
import type { InsightItem } from '../utils/smartInsightsEngine';

interface SmartTooltipProps {
  insights: InsightItem[];
  children: React.ReactNode;
  /** Optional title for the tooltip header */
  title?: string;
}

const SmartTooltip: React.FC<SmartTooltipProps> = ({ insights, children, title }) => {
  const { isEnabled } = useSmartInsights();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback(() => {
    if (!isEnabled || insights.length === 0) return;
    timerRef.current = setTimeout(() => {
      setVisible(true);
    }, 350); // small delay to avoid flickering on quick hover
  }, [isEnabled, insights]);

  const hideTooltip = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  // Calculate position when visible changes
  useEffect(() => {
    if (visible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipWidth = 360;
      const tooltipHeight = Math.min(insights.length * 50 + 60, 320);

      let top = rect.bottom + 8;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;

      // Prevent going off-screen right
      if (left + tooltipWidth > viewportWidth - 16) {
        left = viewportWidth - tooltipWidth - 16;
      }
      // Prevent going off-screen left
      if (left < 16) left = 16;

      // If would go below viewport, show above
      if (top + tooltipHeight > viewportHeight - 16) {
        top = rect.top - tooltipHeight - 8;
      }

      setPosition({ top, left });
    }
  }, [visible, insights.length]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!isEnabled || insights.length === 0) {
    return <>{children}</>;
  }

  const typeColor: Record<InsightItem['type'], string> = {
    positive: 'var(--emerald-light)',
    negative: 'var(--red-light)',
    warning: 'var(--amber-light)',
    info: 'var(--blue-light)',
    neutral: 'var(--text-muted)',
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      style={{ position: 'relative' }}
      className="smart-tooltip-anchor"
    >
      {children}
      {/* Small indicator badge that insights are available */}
      <div className="smart-insight-badge" title="Smart Insights tersedia">
        💡
      </div>
      {visible && (
        <div
          ref={tooltipRef}
          className="smart-tooltip"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 9999,
          }}
          onMouseEnter={() => {
            // Keep tooltip visible when hovering over it
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
          }}
          onMouseLeave={hideTooltip}
        >
          {title && (
            <div className="smart-tooltip-header">
              <span className="smart-tooltip-header-icon">💡</span>
              <span className="smart-tooltip-header-title">Smart Insights — {title}</span>
            </div>
          )}
          <div className="smart-tooltip-body">
            {insights.map((insight, i) => (
              <div key={i} className={`smart-tooltip-item smart-tooltip-item--${insight.type}`}>
                <span className="smart-tooltip-item-icon">{insight.icon}</span>
                <span
                  className="smart-tooltip-item-text"
                  style={{ color: typeColor[insight.type] }}
                >
                  {insight.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartTooltip;
